/* Dedicated views for the 6 Physiology & Biochemistry analyses (Phase 1).
   Deep-teal BKAgriD theme. Uses Recharts.
*/
import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import DOMPurify from 'dompurify';
import { PHYSIO_REFERENCE_VALUES } from '../physiologySampleData';

// XSS defense: citation strings may contain LLM-generated formatting.
// Sanitize before rendering to prevent injection of <script> tags.
const _sanitizeCitation = (html) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
});

const TEAL = '#0F766E';
const TEAL_ACTIVE = '#14B8A6';
const PALETTE = ['#0F766E', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#0D9488', '#134E4A', '#0369A1', '#B45309', '#991B1B'];

const fmt = (v, n = 4) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Citations({ refs }) {
  return (
    <div className="rounded-xl p-4 mt-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }} data-testid="physio-citations">
      <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: TEAL }}>Materials & Methods — References</div>
      <ul className="text-xs space-y-0.5" style={{ color: '#134E4A' }}>
        {(refs || []).map((r, i) => <li key={`citation-${i}-${(r || '').slice(0, 20)}`} dangerouslySetInnerHTML={{ __html: _sanitizeCitation(r) }} />)}
      </ul>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #99F6E4' }} data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: TEAL }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="bg-teal-100 text-teal-900">
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-teal-200 text-left">{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-teal-50'}>
              {keys.map(k => {
                const v = r[k];
                let s;
                if (typeof v === 'boolean') s = v ? 'YES' : 'no';
                else if (typeof v === 'number') s = fmt(v, k.toLowerCase().includes('p_') ? 6 : 4);
                else s = String(v ?? '-');
                return <td key={k} className="px-2 py-1 border border-teal-200 font-mono">{s}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnovaTable({ anova }) {
  if (!anova || !anova.anova) return null;
  return (
    <Table rows={anova.anova} cols={['Source', 'DF', 'SS', 'MS', 'F', 'p_value', 'Sig']} />
  );
}

function StatCard({ label, value, unit }) {
  return (
    <div className="rounded-lg p-3 bg-white border border-teal-200">
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color: TEAL }}>{value}{unit ? <span className="text-[10px] text-gray-500 ml-1">{unit}</span> : null}</div>
    </div>
  );
}

function ReferencePanel({ cropKey, params }) {
  const ref = PHYSIO_REFERENCE_VALUES[cropKey];
  if (!ref) return null;
  return (
    <Card title={`Published Reference Values (${cropKey})`} testid="ref-values">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {(params || Object.keys(ref)).filter(k => ref[k]).map(k => (
          <div key={k} className="flex justify-between text-xs border-b border-teal-100 py-1">
            <span className="text-gray-600">{k.replace(/_/g, ' ')}</span>
            <span className="font-mono font-semibold" style={{ color: TEAL }}>{ref[k]}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-500 mt-2 italic">
        Source: compiled from ICAR/IARI publications &amp; peer-reviewed Indian agricultural journals.
      </p>
    </Card>
  );
}

/* ============================================================
   1. CROP GROWTH ANALYSIS
============================================================ */
export function CropGrowthView({ data }) {
  const rgrChart = useMemo(() => {
    if (!data?.growth_indices_table) return [];
    const intervals = [...new Set(data.growth_indices_table.map(r => r.Interval))];
    return intervals.map(iv => {
      const row = { Interval: iv };
      data.growth_indices_table.filter(r => r.Interval === iv).forEach(r => { row[r.Treatment] = r.RGR_g_g_day; });
      return row;
    });
  }, [data]);
  const biomassChart = useMemo(() => {
    if (!data?.progress_curves) return [];
    const times = [...new Set(data.progress_curves.map(p => p.Time))].sort((a, b) => a - b);
    return times.map(t => {
      const row = { Time: t };
      data.progress_curves.filter(p => p.Time === t).forEach(p => { row[p.Treatment] = p.Dry_weight; });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const treatments = data.treatment_mean_rgr?.map(r => r.Treatment) || [];

  return (
    <div className="space-y-4" data-testid="crop-growth-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Treatments" value={treatments.length} />
        <StatCard label="Best by RGR" value={data.best_treatment_by_RGR || '-'} />
        <StatCard label="RGR ANOVA p" value={fmt(data.anova_rgr?.p_value, 6)} />
        <StatCard label="CGR ANOVA p" value={fmt(data.anova_cgr?.p_value, 6)} />
      </div>

      <Card title="Growth Indices (per interval)" testid="growth-indices-table">
        <Table rows={data.growth_indices_table.slice(0, 60)} cols={['Treatment', 'Interval', 'W1_g', 'W2_g', 'RGR_g_g_day', 'AGR_g_day', 'NAR_g_cm2_day', 'LAR_cm2_g', 'CGR_g_day']} />
      </Card>

      <Card title="Biomass Growth Curve (Dry weight over time)" testid="biomass-chart">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={biomassChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Time" label={{ value: 'Days After Sowing', position: 'insideBottom', offset: -2 }} />
            <YAxis label={{ value: 'Dry weight (g)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {treatments.map((t, i) => <Line key={t} type="monotone" dataKey={t} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 4 }} />)}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Relative Growth Rate (RGR) Across Intervals" testid="rgr-chart">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={rgrChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Interval" />
            <YAxis label={{ value: 'RGR (g/g/day)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {treatments.map((t, i) => <Bar key={t} dataKey={t} fill={PALETTE[i % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="ANOVA — RGR" testid="rgr-anova"><AnovaTable anova={data.anova_rgr} /></Card>
      <Card title="ANOVA — CGR" testid="cgr-anova"><AnovaTable anova={data.anova_cgr} /></Card>

      {data.harvest_index_table && data.harvest_index_table.length > 0 && (
        <Card title="Harvest Index" testid="harvest-index-table">
          <Table rows={data.harvest_index_table} />
        </Card>
      )}

      <Card title="Peak Growth Period per Treatment" testid="peak-growth">
        <Table rows={Object.entries(data.peak_growth_per_treatment || {}).map(([t, p]) => ({ Treatment: t, ...p }))} />
      </Card>

      <Citations refs={[
        'Hunt, R. (1982). <i>Plant Growth Curves: The Functional Approach.</i> Edward Arnold, London.',
        'Radford, P.J. (1967). Growth-analysis formulae — their use and abuse. <i>Crop Science</i> 7: 171–175.',
        'Williams, R.F. (1946). The physiology of plant growth with special reference to the concept of net assimilation rate.',
      ]} />
    </div>
  );
}

/* ============================================================
   2. PHOTOSYNTHESIS & GAS EXCHANGE
============================================================ */
export function PhotosynthesisView({ data }) {
  if (!data) return null;
  const pnGsScatter = data.pn_vs_gs_regression ? null : null; // handled inline below
  return (
    <div className="space-y-4" data-testid="photo-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.pn_ranking?.length || 0} />
        <StatCard label="Best by Pn" value={data.best_genotype_by_Pn || '-'} />
        <StatCard label="Pn-gs R²" value={fmt(data.pn_vs_gs_regression?.R2)} />
        <StatCard label="Yield-Pn R²" value={fmt(data.yield_vs_pn_regression?.R2)} />
      </div>

      <Card title="Gas Exchange Summary" testid="gasex-summary">
        <Table rows={data.summary_table} />
      </Card>

      <Card title="ANOVA per parameter" testid="photo-anovas">
        <div className="space-y-3">
          {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
            <div key={p}>
              <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)} (CV% = {fmt(a.cv_percent, 2)})</div>
              <AnovaTable anova={a} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Correlation Matrix (Pearson r)" testid="photo-corr">
        <Table rows={data.correlation_matrix} />
      </Card>

      {data.pn_vs_gs_regression && (
        <Card title={`Net photosynthesis vs Stomatal conductance — R² = ${fmt(data.pn_vs_gs_regression.R2)}, p = ${fmt(data.pn_vs_gs_regression.p_value, 6)}`} testid="pn-gs-reg">
          <p className="text-xs text-gray-600 font-mono">
            Pn = {data.pn_vs_gs_regression.intercept} + ({data.pn_vs_gs_regression.slope}) × gs  (n = {data.pn_vs_gs_regression.n})
          </p>
        </Card>
      )}
      {data.spad_vs_chl_regression && (
        <Card title={`SPAD vs Extracted Chlorophyll — R² = ${fmt(data.spad_vs_chl_regression.R2)}`} testid="spad-chl-reg">
          <p className="text-xs text-gray-600 font-mono">Chl = {data.spad_vs_chl_regression.intercept} + ({data.spad_vs_chl_regression.slope}) × SPAD</p>
        </Card>
      )}
      {data.yield_vs_pn_regression && (
        <Card title={`Yield vs Net Photosynthesis — R² = ${fmt(data.yield_vs_pn_regression.R2)}`} testid="yield-pn-reg">
          <p className="text-xs text-gray-600 font-mono">Yield = {data.yield_vs_pn_regression.intercept} + ({data.yield_vs_pn_regression.slope}) × Pn</p>
        </Card>
      )}

      <ReferencePanel cropKey="wheat" params={['Net_photosynthesis', 'SPAD', 'Chlorophyll_a', 'Total_chlorophyll']} />

      <Citations refs={[
        'von Caemmerer, S. &amp; Farquhar, G.D. (1981). Some relationships between the biochemistry of photosynthesis and the gas exchange of leaves. <i>Planta</i> 153: 376–387.',
        'Long, S.P. &amp; Bernacchi, C.J. (2003). Gas exchange measurements. <i>J. Exp. Bot.</i> 54: 2393–2401.',
      ]} />
    </div>
  );
}

/* ============================================================
   3. WATER RELATIONS & STRESS
============================================================ */
export function WaterRelationsView({ data }) {
  if (!data) return null;
  const biplotData = (data.stress_indices_table || []).map(r => ({
    x: (r.Y_control + r.Y_stressed) / 2,
    y: r.STI,
    Genotype: r.Genotype,
  }));
  return (
    <div className="space-y-4" data-testid="water-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.stress_indices_table?.length || 0} />
        <StatCard label="Most tolerant (STI)" value={data.best_tolerant_genotype || '-'} />
        <StatCard label="Parameters" value={Object.keys(data.anova_per_parameter || {}).length} />
        <StatCard label="Classification groups" value={new Set((data.classification_table || []).map(r => r.Classification)).size} />
      </div>

      <Card title="Water Relations Parameters" testid="water-table">
        <Table rows={data.water_parameters_table} />
      </Card>

      {data.stress_indices_table?.length > 0 && (
        <Card title="Stress Indices — TOL, MP, GMP, HM, STI, SSI" testid="stress-indices">
          <Table rows={data.stress_indices_table} />
        </Card>
      )}

      {biplotData.length > 0 && (
        <Card title="Biplot — STI vs Mean Yield (identify high-yielding tolerant genotypes)" testid="sti-biplot">
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" name="Mean Yield" label={{ value: 'Mean Yield (t/ha)', position: 'insideBottom', offset: -2 }} />
              <YAxis dataKey="y" name="STI" label={{ value: 'STI', angle: -90, position: 'insideLeft' }} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }}
                content={({ payload }) => {
                  if (payload && payload.length) {
                    const d = payload[0].payload;
                    return <div className="bg-white p-2 border border-teal-200 text-xs">{d.Genotype}: STI={fmt(d.y, 3)}, Mean Yield={fmt(d.x, 2)}</div>;
                  }
                  return null;
                }} />
              <Scatter data={biplotData} fill={TEAL_ACTIVE} />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      )}

      {data.classification_table?.length > 0 && (
        <Card title="Genotype Classification (STI × SSI)" testid="class-table">
          <Table rows={data.classification_table} />
        </Card>
      )}

      <Card title="ANOVA per parameter" testid="water-anovas">
        {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <ReferencePanel cropKey="chickpea" />

      <Citations refs={[
        'Fischer, R.A. &amp; Maurer, R. (1978). Drought resistance in spring wheat cultivars. <i>Aust. J. Agric. Res.</i> 29: 897–912. (SSI)',
        'Fernandez, G.C.J. (1992). Effective selection criteria for assessing plant stress tolerance. <i>Int. Symp. Adapt. of Vegetables</i>: 257–270. (STI, GMP)',
        'Barrs, H.D. &amp; Weatherley, P.E. (1962). A re-examination of the relative turgidity technique for estimating water deficits in leaves.',
      ]} />
    </div>
  );
}

/* ============================================================
   4. CHLOROPHYLL & PIGMENT
============================================================ */
export function ChlorophyllView({ data }) {
  const stageTrend = useMemo(() => {
    if (!data?.stage_wise_trend) return [];
    const stages = [...new Set(data.stage_wise_trend.map(r => r.Stage))];
    return stages.map(s => {
      const row = { Stage: s };
      data.stage_wise_trend.filter(r => r.Stage === s).forEach(r => { row[r.Genotype] = r.Total_Chl; });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const genotypes = [...new Set((data.pigment_table || []).map(r => r.Genotype))];
  return (
    <div className="space-y-4" data-testid="chl-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={genotypes.length} />
        <StatCard label="Best by Total Chl" value={data.best_genotype_by_total_chl || '-'} />
        <StatCard label="Arnon-calc from A663/A645" value={data.has_arnon_calculated ? 'YES' : 'No'} />
        <StatCard label="SPAD-Chl R²" value={fmt(data.spad_vs_extracted_chl_regression?.R2)} />
      </div>

      <Card title="Pigment Concentration (means)" testid="pigment-table">
        <Table rows={data.pigment_table} />
      </Card>

      <Card title="Total Chlorophyll — Stage-wise Trend" testid="stage-trend">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={stageTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Stage" />
            <YAxis label={{ value: 'Total Chl (mg/g FW)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {genotypes.map((g, i) => <Line key={g} type="monotone" dataKey={g} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} />)}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card title="ANOVA per pigment" testid="pig-anovas">
        {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      {data.spad_vs_extracted_chl_regression && (
        <Card title={`SPAD vs Extracted Chlorophyll — R² = ${fmt(data.spad_vs_extracted_chl_regression.R2)}`} testid="spad-chl-reg">
          <p className="text-xs font-mono text-gray-700">Total_Chl = {data.spad_vs_extracted_chl_regression.intercept} + ({data.spad_vs_extracted_chl_regression.slope}) × SPAD</p>
        </Card>
      )}

      <Card title="Correlation Matrix" testid="chl-corr">
        <Table rows={data.correlation_matrix} />
      </Card>

      <ReferencePanel cropKey="wheat" params={['Chlorophyll_a', 'Chlorophyll_b', 'Total_chlorophyll', 'SPAD']} />

      <Citations refs={[
        'Arnon, D.I. (1949). Copper enzymes in isolated chloroplasts: polyphenoloxidase in Beta vulgaris. <i>Plant Physiol.</i> 24: 1–15.',
        'Lichtenthaler, H.K. (1987). Chlorophylls and carotenoids: Pigments of photosynthetic biomembranes. <i>Methods Enzymol.</i> 148: 350–382.',
      ]} />
    </div>
  );
}

/* ============================================================
   5. ENZYME ACTIVITY
============================================================ */
export function EnzymeActivityView({ data }) {
  if (!data) return null;
  const enzymes = data.enzymes_detected || [];
  const treatments = (data.enzyme_activity_table || []).map(r => r.Treatment);
  // Bar chart per enzyme
  const barData = (data.enzyme_activity_table || []);
  return (
    <div className="space-y-4" data-testid="enzyme-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Enzymes" value={enzymes.length} />
        <StatCard label="Treatments" value={treatments.length} />
        <StatCard label="Best defence" value={data.antioxidant_defence_index?.[0]?.Treatment || '-'} />
        <StatCard label="PC1 variance %" value={fmt(data.pca?.variance_explained?.[0], 1)} />
      </div>

      <Card title="Enzyme Activity Table" testid="enzyme-table">
        <Table rows={data.enzyme_activity_table} />
      </Card>

      <Card title="Enzyme Activities — Grouped Bar Chart" testid="enzyme-bar">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Treatment" />
            <YAxis label={{ value: 'Activity units', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {enzymes.map((e, i) => <Bar key={e} dataKey={e} fill={PALETTE[i % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {data.sod_cat_ratio_table?.length > 0 && (
        <Card title="SOD : CAT Ratio (oxidative stress balance)" testid="sod-cat">
          <Table rows={data.sod_cat_ratio_table} />
        </Card>
      )}

      {data.antioxidant_defence_index?.length > 0 && (
        <Card title="Antioxidant Defence Index (normalised composite)" testid="defence-idx">
          <Table rows={data.antioxidant_defence_index} />
        </Card>
      )}

      {data.pca && (
        <Card title={`PCA — Enzyme Activity Profile (PC1 = ${fmt(data.pca.variance_explained[0], 1)}%, PC2 = ${fmt(data.pca.variance_explained[1], 1)}%)`} testid="pca-biplot">
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="PC1" name="PC1" />
              <YAxis dataKey="PC2" name="PC2" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }}
                content={({ payload }) => {
                  if (payload && payload.length) {
                    const d = payload[0].payload;
                    return <div className="bg-white p-2 border border-teal-200 text-xs">{d.Treatment || d.Enzyme}: PC1={fmt(d.PC1, 2)}, PC2={fmt(d.PC2, 2)}</div>;
                  }
                  return null;
                }} />
              <Scatter name="Treatments" data={data.pca.pc_scores} fill={TEAL_ACTIVE} />
              <Scatter name="Enzyme loadings" data={data.pca.loadings.map(l => ({ ...l, Treatment: l.Enzyme }))} fill="#B91C1C" shape="triangle" />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="ANOVA per enzyme" testid="enz-anovas">
        {Object.entries(data.anova_per_enzyme || {}).map(([e, a]) => (
          <div key={e} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{e} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <Card title="Correlation among enzymes" testid="enz-corr">
        <Table rows={data.correlation_matrix} />
      </Card>

      <Citations refs={[
        'Giannopolitis, C.N. &amp; Ries, S.K. (1977). Superoxide dismutases. <i>Plant Physiol.</i> 59: 309–314.',
        'Aebi, H. (1984). Catalase <i>in vitro</i>. <i>Methods Enzymol.</i> 105: 121–126.',
        'Nakano, Y. &amp; Asada, K. (1981). Hydrogen peroxide scavenging by ascorbate peroxidase. <i>Plant Cell Physiol.</i> 22: 867–880.',
      ]} />
    </div>
  );
}

/* ============================================================
   6. OXIDATIVE STRESS MARKERS
============================================================ */
export function OxidativeStressView({ data }) {
  if (!data) return null;
  const mdaByGeno = {};
  const prolineByGeno = {};
  (data.stress_markers_table || []).forEach(r => {
    mdaByGeno[r.Genotype] = mdaByGeno[r.Genotype] || {};
    mdaByGeno[r.Genotype][r.Stress || 'All'] = r.MDA;
    prolineByGeno[r.Genotype] = prolineByGeno[r.Genotype] || {};
    prolineByGeno[r.Genotype][r.Stress || 'All'] = r.Proline;
  });
  const mdaChart = Object.entries(mdaByGeno).map(([g, s]) => ({ Genotype: g, ...s }));
  const prolineChart = Object.entries(prolineByGeno).map(([g, s]) => ({ Genotype: g, ...s }));
  const stressLabels = [...new Set((data.stress_markers_table || []).map(r => r.Stress).filter(Boolean))];
  return (
    <div className="space-y-4" data-testid="oxstress-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.stress_damage_index_ranking?.length || 0} />
        <StatCard label="Most tolerant (low SDI)" value={data.best_tolerant_genotype || '-'} />
        <StatCard label="Markers analysed" value={Object.keys(data.anova_per_marker || {}).length} />
        <StatCard label="Top Proline accum." value={data.proline_accumulation_ratio?.[0]?.Genotype || '-'} />
      </div>

      <Card title="Stress Marker Means" testid="stress-markers-table">
        <Table rows={data.stress_markers_table} />
      </Card>

      <Card title="MDA (lipid peroxidation) — lower = less oxidative damage" testid="mda-chart">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={mdaChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Genotype" />
            <YAxis label={{ value: 'MDA (nmol/g FW)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {stressLabels.map((s, i) => <Bar key={s} dataKey={s} fill={PALETTE[i % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Proline accumulation — higher = better osmotic adjustment" testid="proline-chart">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={prolineChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Genotype" />
            <YAxis label={{ value: 'Proline (µg/g FW)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            {stressLabels.map((s, i) => <Bar key={s} dataKey={s} fill={PALETTE[(i + 3) % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {data.stress_damage_index_ranking?.length > 0 && (
        <Card title="Stress Damage Index Ranking (lower = more tolerant)" testid="sdi-ranking">
          <Table rows={data.stress_damage_index_ranking} />
        </Card>
      )}

      {data.proline_accumulation_ratio?.length > 0 && (
        <Card title="Proline Accumulation Ratio (stressed / control)" testid="proline-ratio">
          <Table rows={data.proline_accumulation_ratio} />
        </Card>
      )}

      <Card title="ANOVA per marker" testid="ox-anovas">
        {Object.entries(data.anova_per_marker || {}).map(([m, a]) => (
          <div key={m} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{m.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <Card title="Correlation Heatmap (stress markers × yield)" testid="ox-corr">
        <Table rows={data.correlation_matrix} />
      </Card>

      <ReferencePanel cropKey="wheat" params={['MDA', 'Proline', 'SOD']} />

      <Citations refs={[
        'Heath, R.L. &amp; Packer, L. (1968). Photoperoxidation in isolated chloroplasts — kinetics &amp; stoichiometry of fatty-acid peroxidation. <i>Arch. Biochem. Biophys.</i> 125: 189–198. (MDA)',
        'Velikova, V. et al. (2000). Oxidative stress and some antioxidant systems in acid rain-treated bean plants. <i>Plant Sci.</i> 151: 59–66. (H₂O₂)',
        'Bates, L.S. et al. (1973). Rapid determination of free proline for water-stress studies. <i>Plant Soil</i> 39: 205–207.',
      ]} />
    </div>
  );
}
